import { SoundSettings } from '../audio/SoundSettings';

/**
 * Settings V1 — the ONE centralized device-local preference module.
 *
 * Product decision (D2): sound and table-chat preferences persist on the
 * device (localStorage), not the backend. This module is the single import
 * surface for preference reads/writes so no screen grows its own competing
 * settings store:
 *
 *   • Sound (enabled + effects volume) DELEGATES to the pre-existing
 *     SoundSettings keys (`sound_enabled`, `sound_effects_volume`) — the
 *     audio managers read those keys directly on every play, so delegating
 *     (instead of duplicating) keeps one storage authority and full
 *     backward compatibility with values users already have stored.
 *   • Table chat owns its key here (`table_chat_enabled`, default ON).
 *     It controls the INITIAL in-game chat presentation only — never the
 *     ActionCable transport or message history.
 *
 * Volume/mute coherence (D3): the volume slider is the single sound
 * control. Setting 0% also flips `sound_enabled` off; any value above 0%
 * flips it back on — so the legacy boolean and the volume can never
 * contradict each other, and every existing consumer of either key keeps
 * working unchanged.
 */
export const PreferenceSettings = {
  // ── Sound (delegated to the existing SoundSettings keys) ──────────────
  /** Effective slider position 0–100: 0 when muted, else the stored volume. */
  getEffectiveVolumePercent(): number {
    if (!SoundSettings.getSoundEnabled()) return 0;
    return Math.round(SoundSettings.getEffectsVolume() * 100);
  },

  /** One write path for the slider: stores volume AND keeps `sound_enabled` in sync. */
  setVolumePercent(percent: number): void {
    const clamped = Math.max(0, Math.min(100, Math.round(percent)));
    SoundSettings.setEffectsVolume(clamped / 100);
    SoundSettings.setSoundEnabled(clamped > 0);
  },

  // ── Table chat ─────────────────────────────────────────────────────────
  getTableChatEnabled(): boolean {
    const raw = localStorage.getItem('table_chat_enabled');
    return raw === null ? true : raw !== 'false';
  },

  setTableChatEnabled(value: boolean): void {
    localStorage.setItem('table_chat_enabled', String(value));
  },
};
